import { useCallback, useEffect, useRef, useState } from 'react';

export interface IUseCarouselOptions {
  rootRef: React.RefObject<HTMLElement | null>;
  trackRef: React.RefObject<HTMLElement | null>;
  prevButtonRef: React.RefObject<HTMLButtonElement | null>;
  nextButtonRef: React.RefObject<HTMLButtonElement | null>;
  cardCount: number;
}

interface ICarouselLayout {
  perView: number;
  maxIndex: number;
  cardWidth: number;
  gap: number;
}

export function useCarousel({ rootRef, trackRef, prevButtonRef, nextButtonRef, cardCount }: IUseCarouselOptions): {
  stepBy: (direction: number) => void;
} {
  const [index, setIndex] = useState(0);
  const layoutRef = useRef<ICarouselLayout>({
    perView: 1,
    maxIndex: 0,
    cardWidth: 0,
    gap: 0,
  });
  const desktopOnly = useRef(typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)') : null).current;

  const readLayout = useCallback((): void => {
    const root = rootRef.current;
    const track = trackRef.current;
    if (!root || !track || !track.children.length) {
      return;
    }

    const styles = getComputedStyle(track);
    const gapValue = parseFloat(styles.gap || '0');
    const viewport = root.clientWidth;
    const perView = viewport >= 1328 ? 4 : 3;
    const maxIndex = Math.max(0, cardCount - perView);
    const cardWidth = (track.children[0] as HTMLElement).getBoundingClientRect().width;

    track.style.setProperty('--cards-per-view', `${perView}`);
    layoutRef.current = { perView, maxIndex, cardWidth, gap: gapValue };
  }, [rootRef, trackRef, cardCount]);

  useEffect(() => {
    readLayout();
  }, [readLayout]);

  useEffect(() => {
    const track = trackRef.current;
    const prevButton = prevButtonRef.current;
    const nextButton = nextButtonRef.current;
    if (!track) {
      return;
    }

    const layout = layoutRef.current;

    if (!desktopOnly?.matches) {
      track.style.transform = 'none';
      if (prevButton) {
        prevButton.disabled = true;
      }
      if (nextButton) {
        nextButton.disabled = true;
      }

      return;
    }

    const offset = (layout.cardWidth + layout.gap) * index;
    track.style.transform = `translateX(-${offset}px)`;
    if (prevButton) {
      prevButton.disabled = index === 0;
    }
    if (nextButton) {
      nextButton.disabled = index >= layout.maxIndex;
    }
  }, [index, trackRef, prevButtonRef, nextButtonRef, desktopOnly]);

  const stepBy = useCallback(
    (direction: number) => {
      if (!desktopOnly?.matches) {
        return;
      }
      setIndex(prev => {
        const layout = layoutRef.current;

        return Math.min(layout.maxIndex, Math.max(0, prev + direction * layout.perView));
      });
    },
    [desktopOnly],
  );

  useEffect(() => {
    const onResize = (): void => {
      readLayout();
      setIndex(prev => {
        const layout = layoutRef.current;

        return Math.min(prev, layout.maxIndex);
      });
    };
    window.addEventListener('resize', onResize);
    desktopOnly?.addEventListener('change', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      desktopOnly?.removeEventListener('change', onResize);
    };
  }, [readLayout, desktopOnly]);

  return { stepBy };
}
